import { CommonModule } from '@angular/common';
import { Component, OnInit, ViewEncapsulation, OnDestroy } from '@angular/core';
import {
    MCButtonComponent,
    MCDialogComponent,
    MCFieldFormComponent,
    McInfoComponent,
} from '@maslul/common-components';
import {
    MCFormisForm,
    MCConfigService,
    MCDialog,
    MCFormValidationService,
    MCFormStateStore,
} from '@maslul/common-services';
import { ProductsStoreService } from '../../../../../services/store/products';
import { ActivatedRoute, ParamMap } from '@angular/router';
import { Subscription, switchMap } from 'rxjs';
import { Guid } from 'guid-typescript';
import { ProductsService } from '../../../../../services/pages/products/products';
import cloneDeep from 'clone-deep';
import { TranslateService } from '@ngx-translate/core';
import { FormlyFieldConfig } from '@ngx-formly/core';
import { GeneralStore } from '../../../../../services/store/general';
import { GeneralSkeletonComponent } from '../../components/general-skeleton/general-skeleton';
import { PageBaseComponent } from '../../../../shared/page-base';
import { FocusOnTabDirective } from '../../../../../services/shared/directives/focus-on-tab';
import { FormControl, FormGroup } from '@angular/forms';

@Component({
    selector: 'mi-products',
    imports: [
        CommonModule,
        MCButtonComponent,
        MCFieldFormComponent,
        McInfoComponent,
        GeneralSkeletonComponent,
        FocusOnTabDirective,
        MCDialogComponent,
    ],
    providers: [],
    templateUrl: './product.html',
    styleUrl: './product.scss',
    encapsulation: ViewEncapsulation.None,
})
export class ProductComponent
    extends PageBaseComponent
    implements OnInit, OnDestroy {
    formisForm: MCFormisForm = new MCFormisForm();
    productGuid: string = '';
    subscription: Subscription = new Subscription();
    isLoading: boolean = false;
    maxProductsReached: boolean = false;
    tabsText: any;
    genericText: any;
    productsText: any;
    productIndex: number = 1;
    productsLength: number = 0;
    productTitle: string = '';
    dialogProperties: MCDialog = new MCDialog();
    isSentRequest: boolean = false;

    constructor(
        private productsStoreService: ProductsStoreService,
        private route: ActivatedRoute,
        private configService: MCConfigService,
        private productsService: ProductsService,
        private formValidationService: MCFormValidationService,
        private translateService: TranslateService,
        private generalStore: GeneralStore,
        private formStateStore: MCFormStateStore

    ) {
        super();
    }

    ngOnInit(): void {
        this.initText();
        this.initDialog();
        this.subscription.add(
            this.generalStore
                .getIsSent()
                .subscribe((isSent) => (this.isSentRequest = isSent)),
        );
    }

    initDialog() {
        this.dialogProperties.breakpoints = {
            '960px': '75vw', // ברוחב 960px ומטה, רוחב הדיאלוג יהיה 75vw
            '640px': '80vw', // ברוחב 640px ומטה, רוחב הדיאלוג יהיה 80
        };
        this.dialogProperties.id = 'mi-remove-product';
    }

    initText() {
        this.subscription.add(
            this.translateService.stream('products').subscribe((translation) => {
                this.productsText = translation;
            }),
        );
        this.subscription.add(
            this.translateService.stream('generic').subscribe((translation) => {
                this.genericText = translation;
            }),
        );
        this.subscription.add(
            this.translateService.stream('tabs').subscribe((translation) => {
                this.tabsText = translation;
                this.initCurrentProduct();
            }),
        );
    }

    resetAllTouchedAndDirtyStatus(): void {
        //קוד לא יפה ואפשר לעלות לקומון
        if (this.formisForm.form instanceof FormGroup) {
            Object.keys(this.formisForm.form.controls).forEach((key) => {
                const control = this.formisForm.form.get(key);
                if (control instanceof FormControl) {
                    control.markAsUntouched();
                } else if (control instanceof FormGroup) {
                    Object.keys(control.controls).forEach((key) => {
                        const subControl = control.get(key) as FormControl;
                        subControl.markAsUntouched();
                        subControl.markAsPristine();
                    });
                }
            });
        }
    }

    initCurrentProduct() {
        this.subscription.add(
            this.route.paramMap
                .pipe(
                    switchMap((params) => {
                        this.isLoading = true;
                        ///this.formisForm.form.markAsUntouched();
                        this.initGuidAndModel(params);
                        //Array.from(this.productsStoreService.getSnapshot().products.keys()).findIndex(g => g ===  this.productGuid)
                        return this.productsService.getFields(
                            this.productGuid!.toString(),
                            this.formisForm,
                        );
                    }),
                )
                .subscribe(
                    (formisFormFields: Array<FormlyFieldConfig> | undefined) => {
                        this.setProductInformations();
                        const rawFormState = this.formStateStore.getSnapshot().formState;
                        this.formisForm.options.formState = typeof rawFormState === 'string'
                            ? JSON.parse(rawFormState || '{}')
                            : cloneDeep(rawFormState || {});
                        if (formisFormFields) {
                            this.formisForm.fields = formisFormFields;
                        }
                        this.maxProductsReached =
                            this.productsService.isMaxProductReached();

                        this.isLoading = false;
                    },
                    (error) => {
                        console.error('Error loading product:', error);
                        this.isLoading = false;
                    },
                ),
        );
    }

    initGuidAndModel(params: ParamMap) {
        const guidParam = params.get('guid');
        this.productGuid = guidParam ? guidParam : Guid.create().toString();
        this.productsStoreService.setActiveProduct(this.productGuid);
        this.formisForm.model =
            this.productsStoreService.getSnapshot().products?.get(this.productGuid)
                ?.formisFormModel || {};
    }

    setProductInformations() {
        this.productTitle = this.tabsText.products;
        const productsFromStore = this.productsStoreService.getSnapshot().products;
        if (productsFromStore) {
            const productsArray = Array.from(productsFromStore.keys());
            this.productsLength = productsArray.length;
            if (this.productsLength > 0) {
                this.productIndex = productsArray.findIndex(
                    (g) => g === this.productGuid,
                );
                if (this.productIndex >= 0) {
                    this.productTitle += ' ' + (this.productIndex + 1);
                } else {
                    this.productTitle += ' ' + (this.productsLength + 1);
                }
            }
        }
    }

    ngOnDestroy(): void {
        this.subscription.unsubscribe();
    }

    saveProduct(): void {
        if (!this.productGuid) {
            console.warn('Cannot save product, product or GUID is missing.');
            return;
        }
        this.productsStoreService.updateProductModel(
            this.productGuid,
            this.formisForm.model,
        );
        this.productsStoreService.updateProductFields(
            this.productGuid,
            this.formisForm.fields,
        );
    }

    createNewProduct(): void {
        const isValid = this.checkIsValidAndMarkAsTouch();

        if (!this.productGuid) {
            return;
        }
        if (
            isValid ||
            this.configService.getConfiguration().noValidCheck == 'true' ||
            (!this.isNewRequest && this.requestId)
        ) {
            this.resetAllTouchedAndDirtyStatus();

            this.saveProduct();
            this.productsService.createNewProductAndNavigate();
        }
    }

    nextTab(): void {
        const isValid = this.checkIsValidAndMarkAsTouch();
        if (!this.productGuid) {
            return;
        }
        if (
            isValid ||
            (!this.isNewRequest && this.requestId) ||
            this.configService.getConfiguration().noValidCheck == 'true'
        ) {
            this.saveProduct();
            this.productsService.navigateToNextProduct();
        }
    }
    next() {
        this.nextTab();
    }

    checkIsValidAndMarkAsTouch(): boolean {
        const isValid = this.formisForm.form.valid;
        const invalidFields = this.formValidationService.getInvalidControlsFlatMap(
            this.formisForm.form,
        );
        console.log('product get invalid field', invalidFields);
        if (isValid) {
            return true;
        } else {
            this.formisForm.form.markAllAsTouched();
            return false;
        }
    }

    back() {
        this.previousTab();
    }

    previousTab(): void {
        if (!this.productGuid) {
            console.warn(
                'Cannot navigate to previous tab, current product or GUID is missing.',
            );
            return;
        }
        this.saveProduct();
        this.productsService.navigateToPreviousProduct();
    }

    maxProductsInfoMessage(): string {
        return this.productsService.maxProductsInfoMessage();
    }

    isLastProduct(): boolean {
        return this.productsService.isLastProduct(this.productGuid || '');
    }

    isSingleProduct() {
        return this.productsLength === 1;
    }

    deleteProduct() {
        this.productsService.showDialogBeforeDeleteProduct(
            this.dialogProperties,
            this.productsText.dialogRemoveProduct,
            this.productGuid,
        );
    }
}
